import { AppPageLayout } from "@/components/common/AppPageLayout";
import { Skeleton } from "@patient-watch/ui/skeleton";
import { useSelectFormById } from "@/features/forms/api/selectFormById";
import { useFormPageCopy } from "@/features/i18n/formPageCopy";
import { useAppLocale } from "@/features/i18n/LocaleProvider";
import type { FormViewT } from "@patient-watch/types";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEntryValuesStore } from "@/features/inputs/hooks/useEntryValuesStore";
import { FormPublicCover } from "@/features/pages/components/FormPublicCover";
import { formPageColumnClass, publicFormRoutePaddingClass } from "@/features/pages/components/formPageLayout";
import { useFormNavigationStore } from "@/features/pages/hooks/useFormNavigationStore";
import {
    formDisplayOptions,
    formInstructionsPlacement,
    formTitleAlign,
    trimmedField,
} from "@/features/pages/utils/formChrome";
import { useState } from "react";

type PageWithInputs = { inputs?: unknown[] };

const FormLandingPage = () => {
    const { formId } = Route.useParams();
    const navigate = useNavigate();
    const { locale } = useAppLocale();
    const copy = useFormPageCopy();
    const { data: form, isLoading } = useSelectFormById(formId, locale);
    const resetEntryValues = useEntryValuesStore((state) => state.resetEntryValues);
    const [isStarting, setIsStarting] = useState(false);

    const handleStartForm = () => {
        if (isStarting) return;
        setIsStarting(true);
        resetEntryValues();

        const placeholderDiaryId = crypto.randomUUID();
        const placeholderTrackerId = crypto.randomUUID();
        const placeholderEntryId = crypto.randomUUID();

        useFormNavigationStore.getState().resetHistory(placeholderEntryId);

        navigate({
            to: "/forms/$formId/diary/$diaryId/tracker/$trackerId/entry/$entryId/page/$pageIndex",
            params: {
                formId,
                diaryId: placeholderDiaryId,
                trackerId: placeholderTrackerId,
                entryId: placeholderEntryId,
                pageIndex: "1",
            },
        });
    };

    if (isLoading) {
        return (
            <AppPageLayout width="md" padding="sm" className={publicFormRoutePaddingClass}>
                <div className={`${formPageColumnClass} gap-4`}>
                    <Skeleton className="w-3/4 h-8" />
                    <Skeleton className="w-full h-4" />
                    <Skeleton className="w-2/3 h-4" />
                </div>
            </AppPageLayout>
        );
    }

    if (!form) return null;

    const f = form as Partial<FormViewT>;

    const numberOfQuestions = Array.isArray(f.pages)
        ? f.pages.reduce(
              (acc: number, page: PageWithInputs) => (Array.isArray(page?.inputs) ? acc + page.inputs.length : acc),
              0
          )
        : 0;

    const name = trimmedField(f.form_name);
    const caption = trimmedField(f.form_caption);
    const kicker = trimmedField(f.form_kicker);
    const copyright = trimmedField(f.copyright_info);
    const coverInstructions = formInstructionsPlacement(f) === "cover" ? trimmedField(f.form_instructions) : null;

    return (
        <AppPageLayout width="md" padding="sm" className={publicFormRoutePaddingClass}>
            <FormPublicCover
                name={name}
                caption={caption}
                kicker={kicker}
                copyright={copyright}
                instructions={coverInstructions}
                timeToComplete={f.time_to_complete || null}
                numberOfQuestions={numberOfQuestions > 0 ? numberOfQuestions : null}
                titleAlign={formTitleAlign(f)}
                showMetrics={formDisplayOptions(f).showCoverMetrics}
                startLabel={isStarting ? copy.startingForm : copy.startForm}
                isStarting={isStarting}
                onStart={handleStartForm}
            />
        </AppPageLayout>
    );
};

export const Route = createFileRoute("/forms/$formId/")({
    component: FormLandingPage,
});
